import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Collapse,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Link,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Logout as LogoutIcon,
  CreditCard as CreditCardIcon,
  Check as CheckIcon,
  Star as StarIcon,
  Garage as GarageIcon,
  AccessTime as AccessTimeIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import LanguageToggle from '../shared/LanguageToggle';
import { getDateFnsLocale } from '../../utils/languages';
import WeekdayHoursEditor from '../shared/WeekdayHoursEditor';
import { DEFAULT_WORK_HOURS, legacyToWorkHours, workHoursError } from '../../utils/weekdayHours';
import axios from 'axios';
import { useUserStore } from '../../store';

const TIMEZONE_OPTIONS = [
  { value: 'Europe/Helsinki', label: 'Finland (Helsinki)' },
  { value: 'Europe/Stockholm', label: 'Sweden (Stockholm)' },
  { value: 'Europe/Oslo', label: 'Norway (Oslo)' },
  { value: 'Europe/Copenhagen', label: 'Denmark (Copenhagen)' },
  { value: 'Europe/Tallinn', label: 'Estonia (Tallinn)' },
  { value: 'Europe/Riga', label: 'Latvia (Riga)' },
  { value: 'Europe/Vilnius', label: 'Lithuania (Vilnius)' },
  { value: 'Europe/Berlin', label: 'Germany (Berlin)' },
  { value: 'Europe/London', label: 'UK (London)' },
];

const ONBOARDING_STORAGE_KEY = 'verstaani_onboarding_state';

const DEFAULT_FORM_DATA = {
  // Step 1: Business ID
  business_id: '',
  // Step 2: Organization info (name from YTJ dropdown)
  organization_name: '',
  address_street: '',
  address_postal_code: '',
  address_city: '',
  address_country: 'FI',
  phone: '',
  // Step 3: Contact person
  contact_name: '',
  contact_phone: '',
  // Step 4: Settings (margin + optional billing + hourly work)
  default_parts_margin: 25,
  hourly_work_price: '',
  hourly_work_vat: '25.5',
  iban: '',
  bic: '',
  vat_number: '',
  invoice_email: '',
  // Step 5: Schedule
  timezone: 'Europe/Helsinki',
  work_hours: DEFAULT_WORK_HOURS,
  // Optional features
  service_bays_enabled: false,
  work_hours_reporting_enabled: false,
  inventory_tracking_enabled: false,
  // Language preferences
  preferred_language: 'fi',
  default_language: 'fi',
  // Step 6: Subscription
  selected_plan_id: null, // null means trial
  billing_period: 'monthly',
};

// Load saved onboarding state from localStorage
const loadSavedState = () => {
  try {
    const saved = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Failed to load onboarding state from localStorage:', e);
  }
  return null;
};

export default function OnboardingWizard() {
  const { t, i18n } = useTranslation();
  const { fetchUser } = useUserStore();

  // Initialize state from localStorage if available
  const [activeStep, setActiveStep] = useState(() => {
    const saved = loadSavedState();
    return saved?.activeStep ?? 0;
  });
  const [loading, setLoading] = useState(false);
  const [ytjLoading, setYtjLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ytjError, setYtjError] = useState(null);
  const [showBillingSettings, setShowBillingSettings] = useState(() => {
    const saved = loadSavedState();
    return saved?.showBillingSettings ?? false;
  });

  // YTJ validated data - names the user can choose from
  const [ytjValidated, setYtjValidated] = useState(() => {
    const saved = loadSavedState();
    return saved?.ytjValidated ?? false;
  });
  const [availableNames, setAvailableNames] = useState(() => {
    const saved = loadSavedState();
    return saved?.availableNames ?? [];
  });
  // Manual company-info entry - unlocked only when the YTJ lookup service is unavailable
  const [manualMode, setManualMode] = useState(() => {
    const saved = loadSavedState();
    return saved?.manualMode ?? false;
  });

  // Form data
  const [formData, setFormData] = useState(() => {
    const saved = loadSavedState();
    if (!saved?.formData) return { ...DEFAULT_FORM_DATA };
    // A wizard state saved before per-weekday hours has the old range + period instead
    if (!saved.formData.work_hours) {
      const { work_week_start, work_week_end, work_day_start, work_day_end } = saved.formData;
      return {
        ...saved.formData,
        work_hours: work_week_start !== undefined
          ? legacyToWorkHours(work_week_start, work_week_end, work_day_start, work_day_end)
          : DEFAULT_WORK_HOURS,
      };
    }
    return saved.formData;
  });

  // State for subscription plans
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const steps = [
    { label: t('onboarding.steps.businessId', 'Business ID'), icon: <SearchIcon /> },
    { label: t('onboarding.steps.companyInfo', 'Company Info'), icon: <BusinessIcon /> },
    { label: t('onboarding.steps.contactPerson', 'Contact Person'), icon: <PersonIcon /> },
    { label: t('onboarding.steps.settings', 'Settings'), icon: <SettingsIcon /> },
    { label: t('onboarding.steps.schedule', 'Schedule'), icon: <ScheduleIcon /> },
    { label: t('onboarding.steps.subscription', 'Subscription'), icon: <CreditCardIcon /> },
  ];

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    const stateToSave = {
      activeStep,
      formData,
      ytjValidated,
      availableNames,
      manualMode,
      showBillingSettings,
    };
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (e) {
      console.warn('Failed to save onboarding state to localStorage:', e);
    }
  }, [activeStep, formData, ytjValidated, availableNames, manualMode, showBillingSettings]);

  // Sync i18n language with formData on mount
  useEffect(() => {
    if (formData.preferred_language && i18n.language !== formData.preferred_language) {
      i18n.changeLanguage(formData.preferred_language);
      localStorage.setItem('userLanguage', formData.preferred_language);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch subscription plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      try {
        const response = await axios.get('/api/payments/plans/');
        // Filter out trial plan from display, but keep others
        const availablePlans = (response.data.plans || []).filter(plan => !plan.is_trial_plan);
        setPlans(availablePlans);
      } catch (err) {
        console.warn('Failed to fetch plans:', err);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleLanguageChange = useCallback((event, newLang) => {
    if (!newLang) return;
    i18n.changeLanguage(newLang);
    localStorage.setItem('userLanguage', newLang);
    setFormData(prev => ({
      ...prev,
      preferred_language: newLang,
      default_language: newLang,
    }));
  }, [i18n]);

  const dateLocale = getDateFnsLocale(formData.preferred_language);

  const lookupYTJ = async () => {
    if (!formData.business_id || formData.business_id.length < 9) {
      setYtjError(t('onboarding.errors.invalidBusinessId', 'Please enter a valid business ID (e.g., 1234567-8)'));
      return;
    }

    setYtjLoading(true);
    setYtjError(null);
    setYtjValidated(false);

    try {
      const response = await axios.get('/api/ytj-lookup/', {
        params: { business_id: formData.business_id }
      });

      const data = response.data;

      // Store available names from YTJ
      const names = data.names || [];
      setAvailableNames(names);

      // Auto-select official name or first available
      const officialName = names.find(n => n.is_official)?.name || names[0]?.name || '';

      // Generate VAT number from business ID (FI + digits without dash)
      const vatNumber = 'FI' + formData.business_id.replace('-', '');

      setFormData(prev => ({
        ...prev,
        organization_name: officialName,
        address_street: data.address_street || prev.address_street,
        address_postal_code: data.address_postal_code || prev.address_postal_code,
        address_city: data.address_city || prev.address_city,
        address_country: data.address_country || prev.address_country || 'FI',
        vat_number: vatNumber,
      }));

      // Mark as validated
      setYtjValidated(true);
      setManualMode(false);

      // Auto-advance to next step if we got data
      if (officialName) {
        setActiveStep(1);
      }
    } catch (err) {
      if (err.response?.status === 503 || err.response?.data?.error_code === 'lookup_unavailable') {
        // YTJ lookup is broken (not an authoritative "not found") -
        // unlock manual company-info entry so onboarding is never blocked
        setManualMode(true);
        setYtjValidated(false);
        setAvailableNames([]);
        setFormData(prev => ({
          ...prev,
          vat_number: 'FI' + prev.business_id.replace('-', ''),
        }));
        setActiveStep(1);
        return;
      }
      if (err.response?.status === 404) {
        setYtjError(t('onboarding.errors.companyNotFound', 'No company found with this business ID'));
      } else if (err.response?.status === 409) {
        setYtjError(t('onboarding.errors.businessIdTaken', 'An organization with this business ID already exists.'));
      } else {
        setYtjError(err.response?.data?.error || t('onboarding.errors.ytjFailed', 'Failed to fetch company info'));
      }
      setYtjValidated(false);
      setAvailableNames([]);
    } finally {
      setYtjLoading(false);
    }
  };

  // Reset YTJ validation when business ID changes
  const handleBusinessIdChange = (value) => {
    updateFormData('business_id', value);
    if (ytjValidated || manualMode) {
      setYtjValidated(false);
      setManualMode(false);
      setAvailableNames([]);
      setFormData(prev => ({
        ...prev,
        organization_name: '',
        address_street: '',
        address_postal_code: '',
        address_city: '',
        address_country: 'FI',
      }));
    }
  };

  const handleNext = async () => {
    setError(null);

    // Step 0: Validate and lookup from YTJ
    if (activeStep === 0) {
      if (!formData.business_id) {
        setError(t('onboarding.errors.businessIdRequired', 'Business ID is required'));
        return;
      }
      // Do YTJ lookup and advance if successful
      await lookupYTJ();
      return; // lookupYTJ handles advancing to next step
    }

    // Step 1: Company info validation
    if (activeStep === 1) {
      if (!formData.organization_name) {
        setError(t('onboarding.errors.orgNameRequired', 'Organization name is required'));
        return;
      }
      if (!formData.phone) {
        setError(t('onboarding.errors.phoneRequired', 'Company phone number is required'));
        return;
      }
      // Prefill contact phone with company phone if not already set
      if (!formData.contact_phone && formData.phone) {
        updateFormData('contact_phone', formData.phone);
      }
    }

    // Step 2: Contact person validation
    if (activeStep === 2) {
      if (!formData.contact_name) {
        setError(t('onboarding.errors.contactNameRequired', 'Contact name is required'));
        return;
      }
      if (!formData.contact_phone) {
        setError(t('onboarding.errors.contactPhoneRequired', 'Contact phone number is required'));
        return;
      }
    }

    // Step 4: Work hours
    if (activeStep === 4) {
      const workHoursProblem = workHoursError(formData.work_hours);
      if (workHoursProblem) {
        setError(t(workHoursProblem));
        return;
      }
    }

    setActiveStep(prev => prev + 1);
  };

  // Check if business ID looks valid (basic format check for button enable)
  const isBusinessIdValid = () => {
    const id = formData.business_id;
    return id && /^\d{7}-\d$/.test(id);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/accounts/logout/';
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Create the organization (with trial subscription by default)
      await axios.post('/api/onboarding/', formData);

      // Clear saved onboarding state on success
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);

      // Step 2: If a paid plan was selected, redirect to Stripe checkout
      if (formData.selected_plan_id !== null) {
        try {
          const checkoutResponse = await axios.post('/api/payments/checkout/', {
            plan_id: formData.selected_plan_id,
            billing_period: formData.billing_period,
          });

          // Redirect to Stripe checkout
          if (checkoutResponse.data.checkout_url) {
            window.location.href = checkoutResponse.data.checkout_url;
            return; // Don't call fetchUser, let Stripe handle the redirect
          }
        } catch (checkoutErr) {
          // If checkout fails, the org is still created with trial
          // Log the error but continue - user can upgrade later
          console.warn('Failed to start checkout, continuing with trial:', checkoutErr);
        }
      }

      // Refetch user to update the state (for trial or if checkout failed)
      await fetchUser();
    } catch (err) {
      const errors = err.response?.data;
      if (errors) {
        // Format validation errors
        const errorMessages = Object.entries(errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        setError(errorMessages);
      } else {
        setError(t('onboarding.errors.submitFailed', 'Failed to complete setup. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('onboarding.businessId.title', 'Enter your Business ID')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('onboarding.businessId.description', 'We will fetch your company information from the Finnish Business Information System (YTJ).')}
            </Typography>

            <TextField
              fullWidth
              label={t('onboarding.fields.businessId', 'Business ID (Y-tunnus)')}
              placeholder="1234567-8"
              value={formData.business_id}
              onChange={(e) => handleBusinessIdChange(e.target.value)}
              error={!!ytjError}
              helperText={ytjError}
              sx={{ mb: 2 }}
            />

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {t('onboarding.businessId.legalTitle', 'Legal Notice')}
              </Typography>
              <Typography variant="body2">
                {t('onboarding.businessId.legalNotice', 'By entering this business ID, you confirm that you are an authorized representative of this organization. Impersonating a company you do not represent is illegal and may result in legal action.')}
              </Typography>
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {t('onboarding.businessId.termsConsent', 'By entering your business ID and proceeding, you agree to our')}{' '}
              <Link href="https://verstaani.fi/kayttoehdot/" target="_blank" rel="noopener noreferrer">
                {t('common.termsOfUse', 'Terms of Use')}
              </Link>
              {' '}{t('common.and', 'and')}{' '}
              <Link href="https://verstaani.fi/tietosuoja/" target="_blank" rel="noopener noreferrer">
                {t('common.privacyPolicy', 'Privacy Policy')}
              </Link>.
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('onboarding.companyInfo.title', 'Company Information')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('onboarding.companyInfo.description', 'Select your company name and enter contact details.')}
            </Typography>

            {manualMode && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {t('onboarding.manualEntry.banner', 'Company details could not be fetched from the YTJ service right now. You can enter the company name and address manually. Please make sure the name matches your registered company name.')}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                {manualMode ? (
                  <TextField
                    fullWidth
                    required
                    label={t('onboarding.fields.organizationName', 'Company Name')}
                    value={formData.organization_name}
                    onChange={(e) => updateFormData('organization_name', e.target.value)}
                    helperText={t('onboarding.manualEntry.nameHelp', "Enter the company's official name")}
                  />
                ) : availableNames.length > 1 ? (
                  <FormControl fullWidth required>
                    <InputLabel>{t('onboarding.fields.organizationName', 'Company Name')}</InputLabel>
                    <Select
                      value={formData.organization_name}
                      label={t('onboarding.fields.organizationName', 'Company Name')}
                      onChange={(e) => updateFormData('organization_name', e.target.value)}
                    >
                      {availableNames.map((nameObj) => (
                        <MenuItem key={nameObj.name} value={nameObj.name}>
                          {nameObj.name}
                          {nameObj.is_official && (
                            <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.85em' }}>
                              ({t('onboarding.companyInfo.officialName', 'Official')})
                            </Typography>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    required
                    label={t('onboarding.fields.organizationName', 'Company Name')}
                    value={formData.organization_name}
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText={t('onboarding.companyInfo.nameFromYTJ', 'Name from YTJ registry')}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('onboarding.fields.streetAddress', 'Street Address')}
                  value={formData.address_street}
                  onChange={(e) => updateFormData('address_street', e.target.value)}
                  disabled={!manualMode}
                  InputProps={{
                    readOnly: !manualMode,
                  }}
                  helperText={manualMode
                    ? t('onboarding.manualEntry.addressHelp', 'Enter the address manually')
                    : t('onboarding.companyInfo.addressFromYTJ', 'Address from YTJ registry')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('onboarding.fields.postalCode', 'Postal Code')}
                  value={formData.address_postal_code}
                  onChange={(e) => updateFormData('address_postal_code', e.target.value)}
                  disabled={!manualMode}
                  InputProps={{
                    readOnly: !manualMode,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('onboarding.fields.city', 'City')}
                  value={formData.address_city}
                  onChange={(e) => updateFormData('address_city', e.target.value)}
                  disabled={!manualMode}
                  InputProps={{
                    readOnly: !manualMode,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label={t('onboarding.fields.phone', 'Company Phone')}
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  placeholder="+358 40 123 4567"
                  helperText={t('onboarding.companyInfo.phoneHelp', 'Contact phone for your workshop')}
                  autoComplete="tel"
                  type="tel"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('onboarding.contactPerson.title', 'Contact Person')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('onboarding.contactPerson.description', 'Enter your contact details. This will also create your employee profile.')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label={t('onboarding.fields.contactName', 'Your Name')}
                  value={formData.contact_name}
                  onChange={(e) => updateFormData('contact_name', e.target.value)}
                  autoComplete="name"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label={t('onboarding.fields.contactPhone', 'Your Phone Number')}
                  value={formData.contact_phone}
                  onChange={(e) => updateFormData('contact_phone', e.target.value)}
                  placeholder="+358 40 123 4567"
                  autoComplete="tel"
                  type="tel"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('onboarding.settings.title', 'Organization Settings')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('onboarding.settings.description', 'Configure your workshop settings. You can change these later in Settings.')}
            </Typography>

            {/* Default Language */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              {t('onboarding.settings.defaultLanguage', 'Organization Default Language')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <LanguageSelect
                  fullWidth
                  value={formData.default_language}
                  label={t('onboarding.settings.defaultLanguage', 'Organization Default Language')}
                  onChange={(e) => updateFormData('default_language', e.target.value)}
                />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
              {t('onboarding.settings.defaultLanguageHelp', 'Sets the default language for communications (emails, SMS, invitations) and for new team members who haven\'t set a personal preference.')}
            </Alert>

            <Divider sx={{ mb: 3 }} />

            {/* Pricing Section */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              {t('onboarding.settings.pricingSection', 'Pricing')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('onboarding.fields.defaultPartsMargin', 'Default Parts Margin')}
                  type="number"
                  value={formData.default_parts_margin}
                  onChange={(e) => updateFormData('default_parts_margin', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('onboarding.settings.marginHelp', 'Percentage added to parts purchase price when selling to customers. For example, 25% margin means a part you buy for 100€ will be sold for 125€.')}
            </Alert>

            {/* Hourly Work Price */}
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('onboarding.fields.hourlyWorkPrice', 'Hourly work price')}
                    type="number"
                    value={formData.hourly_work_price}
                    onChange={(e) => updateFormData('hourly_work_price', e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                    placeholder={t('onboarding.settings.hourlyPricePlaceholder', 'e.g. 75')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('onboarding.settings.priceIncludesVat', 'Price includes VAT')}
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.hourly_work_vat}
                    exclusive
                    onChange={(e, value) => value && updateFormData('hourly_work_vat', value)}
                    size="small"
                  >
                    <ToggleButton value="0">
                      {t('onboarding.settings.vatZero', 'VAT 0%')}
                    </ToggleButton>
                    <ToggleButton value="25.5">
                      {t('onboarding.settings.vatStandard', 'VAT 25.5%')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('onboarding.settings.hourlyWorkInfo', 'If you enter an hourly price, a job called "Tuntityö" (Hourly work) will be created for your workshop. You can use this when adding work to maintenance orders. Leave empty to skip.')}
              </Alert>
            </Box>

            {/* Optional Billing Settings */}
            <Box sx={{ mt: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showBillingSettings}
                    onChange={(e) => setShowBillingSettings(e.target.checked)}
                  />
                }
                label={t('onboarding.billing.enableBilling', 'I want to set up invoice settings now')}
              />

              <Collapse in={showBillingSettings}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label={t('onboarding.fields.iban', 'IBAN')}
                      placeholder="FI00 0000 0000 0000 00"
                      value={formData.iban}
                      onChange={(e) => updateFormData('iban', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={t('onboarding.fields.bic', 'BIC/SWIFT')}
                      placeholder="NDEAFIHH"
                      value={formData.bic}
                      onChange={(e) => updateFormData('bic', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('onboarding.fields.vatNumber', 'VAT Number')}
                      placeholder="FI12345678"
                      value={formData.vat_number}
                      onChange={(e) => updateFormData('vat_number', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('onboarding.fields.invoiceEmail', 'Invoice Email')}
                      type="email"
                      value={formData.invoice_email}
                      onChange={(e) => updateFormData('invoice_email', e.target.value)}
                      autoComplete="email"
                    />
                  </Grid>
                </Grid>
              </Collapse>

              {!showBillingSettings && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('onboarding.billing.skipHint', 'You can set up invoice settings later in Settings > Invoice Settings.')}
                </Alert>
              )}
            </Box>

            {/* Optional Features */}
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {t('onboarding.features.title', 'Optional Features')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('onboarding.features.description', 'Enable optional features for your workshop. You can change these later in Settings.')}
              </Typography>

              {/* Service Bays */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.service_bays_enabled}
                      onChange={(e) => updateFormData('service_bays_enabled', e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GarageIcon fontSize="small" color="action" />
                      {t('onboarding.features.serviceBays', 'Service Bays')}
                    </Box>
                  }
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('onboarding.features.serviceBaysDescription', 'Enable if your workshop has multiple service bays or lifts. Allows you to assign maintenance orders to specific bays and see bay availability in the calendar.')}
                </Alert>
              </Box>

              {/* Work Time Management */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.work_hours_reporting_enabled}
                      onChange={(e) => updateFormData('work_hours_reporting_enabled', e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon fontSize="small" color="action" />
                      {t('onboarding.features.workTime', 'Work Time Management')}
                    </Box>
                  }
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('onboarding.features.workTimeDescription', 'Track employee work hours and generate work time reports. Employees can log their daily hours and managers can review and approve them.')}
                </Alert>
              </Box>

              {/* Inventory Management */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.inventory_tracking_enabled}
                      onChange={(e) => updateFormData('inventory_tracking_enabled', e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InventoryIcon fontSize="small" color="action" />
                      {t('onboarding.features.inventory', 'Inventory Management')}
                    </Box>
                  }
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('onboarding.features.inventoryDescription', 'Track parts stock levels in your warehouse. Receive parts into inventory, and stock is automatically updated when parts are used in maintenance orders.')}
                </Alert>
              </Box>
            </Box>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('onboarding.schedule.title', 'Work Schedule')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('onboarding.schedule.description', 'Configure your workshop\'s working hours for the booking calendar.')}
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {t('onboarding.schedule.infoTitle', 'What this affects')}
              </Typography>
              <Typography variant="body2">
                {t('onboarding.schedule.infoText', 'These settings determine which days and hours are visible in the booking calendar, and are used to autofill work time entries for employees. Individual employees can override their personal work hours in their profile settings.')}
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('onboarding.fields.timezone', 'Timezone')}</InputLabel>
                  <Select
                    value={formData.timezone}
                    label={t('onboarding.fields.timezone', 'Timezone')}
                    onChange={(e) => updateFormData('timezone', e.target.value)}
                  >
                    {TIMEZONE_OPTIONS.map(tz => (
                      <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('onboarding.schedule.workHours', 'Work Hours')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('workHours.editorHelp')}
                </Typography>
                <WeekdayHoursEditor
                  value={formData.work_hours}
                  onChange={(workHours) => updateFormData('work_hours', workHours)}
                  dateLocale={dateLocale}
                  testIdPrefix="onboarding-work-hours"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('onboarding.subscription.title', 'Choose Your Plan')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('onboarding.subscription.description', 'Select a subscription plan. You can start with a free trial and upgrade anytime.')}
            </Typography>

            {plansLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Trial Option */}
                <Card
                  variant={formData.selected_plan_id === null ? 'elevation' : 'outlined'}
                  elevation={formData.selected_plan_id === null ? 4 : 0}
                  sx={{
                    mb: 3,
                    cursor: 'pointer',
                    border: formData.selected_plan_id === null ? '2px solid' : '1px solid',
                    borderColor: formData.selected_plan_id === null ? 'primary.main' : 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                  onClick={() => updateFormData('selected_plan_id', null)}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {t('onboarding.subscription.trialTitle', 'Free Trial')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('onboarding.subscription.trialDescription', '14-day free trial with full access to all features. No credit card required.')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {t('onboarding.subscription.free', 'Free')}
                      </Typography>
                      {formData.selected_plan_id === null && (
                        <CheckCircleIcon color="primary" />
                      )}
                    </Box>
                  </CardContent>
                </Card>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('onboarding.subscription.orChoosePlan', 'or choose a paid plan')}
                  </Typography>
                </Divider>

                {/* Billing Period Toggle */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <ToggleButtonGroup
                    value={formData.billing_period}
                    exclusive
                    onChange={(e, value) => value && updateFormData('billing_period', value)}
                    aria-label={t('Billing period')}
                  >
                    <ToggleButton value="monthly" aria-label={t('Monthly')}>
                      {t('common.monthly', 'Monthly')}
                    </ToggleButton>
                    <ToggleButton value="yearly" aria-label={t('Yearly')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {t('common.yearly', 'Yearly')}
                        <Chip label={t('onboarding.subscription.save17', 'Save 17%')} size="small" color="success" />
                      </Box>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Plan Cards */}
                <Grid container spacing={2}>
                  {plans.map((plan) => {
                    const price = formData.billing_period === 'yearly' ? plan.price_yearly : plan.price_monthly;
                    const monthlyEquivalent = formData.billing_period === 'yearly' ? plan.price_yearly / 12 : plan.price_monthly;
                    const isSelected = formData.selected_plan_id === plan.id;

                    return (
                      <Grid item xs={12} md={4} key={plan.id}>
                        <Card
                          variant={isSelected ? 'elevation' : 'outlined'}
                          elevation={isSelected ? 4 : 0}
                          sx={{
                            height: '100%',
                            cursor: 'pointer',
                            position: 'relative',
                            border: isSelected ? '2px solid' : plan.is_highlighted ? '2px solid' : '1px solid',
                            borderColor: isSelected ? 'primary.main' : plan.is_highlighted ? 'primary.light' : 'divider',
                            transition: 'all 0.2s',
                            overflow: 'visible',
                            '&:hover': {
                              borderColor: 'primary.main',
                            },
                          }}
                          onClick={() => updateFormData('selected_plan_id', plan.id)}
                        >
                          {/* Most Popular Badge */}
                          {plan.is_highlighted && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -12,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 1,
                              }}
                            >
                              <Chip
                                icon={<StarIcon />}
                                label={t('onboarding.subscription.mostPopular', 'Most Popular')}
                                color="primary"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                          )}

                          <CardContent sx={{ pt: plan.is_highlighted ? 4 : 3 }}>
                            {/* Plan Name */}
                            <Typography variant="h6" fontWeight={600} textAlign="center" gutterBottom>
                              {plan.display_name}
                            </Typography>

                            {/* Description */}
                            {plan.description && (
                              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2, minHeight: 40 }}>
                                {plan.description}
                              </Typography>
                            )}

                            {/* Price */}
                            <Box sx={{ textAlign: 'center', mb: 2 }}>
                              <Typography variant="h4" fontWeight={700} color="primary">
                                {new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(monthlyEquivalent)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                / {t('common.month', 'month')}
                              </Typography>
                              {formData.billing_period === 'yearly' && (
                                <Typography variant="caption" color="text.secondary">
                                  {new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(price)} {t('common.billedYearly', 'billed yearly')}
                                </Typography>
                              )}
                            </Box>

                            {/* Seats */}
                            <Box sx={{ textAlign: 'center', mb: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {plan.is_unlimited_seats
                                  ? t('onboarding.subscription.unlimitedUsers', 'Unlimited users')
                                  : t('onboarding.subscription.upToUsers', 'Up to {{count}} users', { count: plan.max_seats })}
                              </Typography>
                            </Box>

                            {/* Features */}
                            {Array.isArray(plan.features) && plan.features.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                {plan.features.map((feature, index) => (
                                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <CheckIcon color="success" fontSize="small" />
                                    <Typography variant="body2">
                                      {feature}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {/* Selection Indicator */}
                            {isSelected && (
                              <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <CheckCircleIcon color="primary" />
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Notice about metered billing */}
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    {t('onboarding.subscription.vatNotice', 'All prices exclude VAT. SMS and e-invoice usage is billed separately based on actual usage.')}
                  </Typography>
                </Alert>
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        {/* Language toggle + Logout button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: 1 }}>
          <LanguageToggle
            value={formData.preferred_language}
            onChange={handleLanguageChange}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 2 } }}
          />
          <Button
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            color="inherit"
            sx={{ color: 'text.secondary' }}
          >
            {t('common.logout', 'Log out')}
          </Button>
        </Box>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/img/logo-smaller.png"
            alt="Verstaani"
            sx={{ height: 48, mb: 2 }}
          />
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {t('onboarding.welcome', 'Welcome to Verstaani')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('onboarding.subtitle', 'Let\'s set up your workshop in just a few steps')}
          </Typography>
        </Box>

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                        color: 'white',
                      }}
                    >
                      {index < activeStep ? <CheckCircleIcon /> : step.icon}
                    </Box>
                  )}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Content */}
        <Paper sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {renderStepContent()}

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              {t('common.back', 'Back')}
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {loading
                  ? <span>{t('common.loading', 'Loading...')}</span>
                  : formData.selected_plan_id === null
                    ? <span>{t('onboarding.actions.startTrial', 'Start Trial')}</span>
                    : <span>{t('onboarding.actions.subscribeAndFinish', 'Subscribe & Finish')}</span>
                }
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={activeStep === 0 ? (!isBusinessIdValid() || ytjLoading) : false}
                startIcon={ytjLoading && activeStep === 0 ? <CircularProgress size={20} /> : null}
              >
                {ytjLoading && activeStep === 0 ? <span>{t('common.loading', 'Loading...')}</span> : <span>{t('common.next', 'Next')}</span>}
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
